import { useEffect, useRef } from 'react';
import { useRouter, useRouterState } from '@tanstack/react-router';
import {
  loginFromUrlSecret,
  resetLocalStateForProfileSwap,
  whoAmI,
  NoAdminProfileError,
} from '../../services/authService';
import { useAuthStore } from '../../store/useAuthStore';
import { useProfileStore } from '../../store/useProfileStore';
import { openExpiredLinkModal } from '../../services/expiredLinkModalService';
import { showErrorModal } from '../../services/errorModalService';
import { isApiError } from '../../lib/api/errors';
import { logDebug, logError } from '../../services/loggerService';

/**
 * Handles links carrying `?s=<secret>` (alert emails / SMS deep links into
 * admin views). Mounted at the router root; the authenticated layout lets
 * `?s=` URLs through instead of redirecting to sign-in, so this can run.
 *
 *   1. Wait until the boot session restore settles.
 *   2. POST /login-secret — the BE sets fresh session cookies.
 *   3. If the link is for a different profile than the current one, reset
 *      local state WITHOUT /auth/logout (which would clear the new cookies).
 *   4. whoAmI for the linked profile, then strip `?s=` from the URL.
 *   5. 401 → the expired-link modal offers a resend.
 *
 * Guarded per secret value so StrictMode's double mount doesn't log in twice.
 */
export function SecretLoginHandler() {
  const router = useRouter();
  const location = useRouterState({ select: (s) => s.location });
  const restoring = useAuthStore((s) => s.sessionStatus === 'restoring');
  const activeProfileId = useProfileStore((s) => s.activeProfile?.id ?? null);
  const inFlightSecret = useRef<string | null>(null);

  useEffect(() => {
    if (restoring) return;
    const params = new URLSearchParams(location.searchStr);
    const secret = params.get('s');
    if (!secret || inFlightSecret.current === secret) return;
    inFlightSecret.current = secret;

    const cleanUrl = () => {
      params.delete('s');
      const qs = params.toString();
      router.history.replace(location.pathname + (qs ? `?${qs}` : ''));
    };

    void (async () => {
      try {
        const response = await loginFromUrlSecret(secret);
        logDebug('Secret login succeeded', undefined, {
          incomingProfileId: response.profileId,
          previousProfileId: activeProfileId,
        });
        if (activeProfileId !== response.profileId) resetLocalStateForProfileSwap();
        useAuthStore.getState().setAuthenticated(true);
        useAuthStore.getState().setSessionStatus('restoring');
        await whoAmI(response.profileId);
        cleanUrl();
      } catch (err) {
        const status = isApiError(err) ? err.status : undefined;
        logError(err instanceof Error ? err : new Error(String(err)), 'Secret login failed', undefined, { status });
        if (status === 401) {
          // Keep `?s=` until the modal closes so a refresh keeps the context.
          openExpiredLinkModal(secret);
          inFlightSecret.current = null;
          return;
        }
        cleanUrl();
        // NoAdminProfileError is shown by the session gate, not a modal.
        if (!(err instanceof NoAdminProfileError) && isApiError(err)) {
          showErrorModal(err.userMessage, err.correlationId, err.code);
        }
      }
    })();
  }, [location.searchStr, location.pathname, restoring, activeProfileId, router]);

  return null;
}
